import { useRequestGetPaymentProviders, useRequestSetPaymentProviders, useRequestDeletePaymentOperator } from '@/hooks/httpRequest';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, Switch, Button, Badge, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui';
import ProviderBadge from '@/components/custom/profileDetailUserData/profileDetailUserDataPayments/components/ProviderBadge';
import { notify, emptyFunction } from '@/functions';
import type { IPaymentProvider, PaymentProviderTypeEnum } from '@/interfaces/paymentProvider.interface';
import { HeaderPage } from '../components';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { countries } from '@/config';
import { Pencil, Trash2, Plus } from 'lucide-react';
import OperatorFormModal from './OperatorFormModal';

const providerKey = (provider: IPaymentProvider) =>
  `${provider.provider}_${provider.platform}`;

const CONTINENT_LABELS: Record<string, string> = {
  AFRICA: 'Afrique',
  EUROPE: 'Europe',
  'NORTH AMERICA': 'Amérique du Nord',
  'SOUTH AMERICA': 'Amérique du Sud',
  ASIA: 'Asie',
  OCEANIA: 'Océanie',
  ANTARCTICA: 'Antarctique',
};

/* ISO2 to provider-config continent string. Covers the countries active in the payment system.
   i18n-iso-countries does not expose continent data, so this mirrors the API's mapping. */
const ISO2_TO_CONTINENT: Record<string, string> = {
  BF: 'AFRICA', BJ: 'AFRICA', CD: 'AFRICA', CG: 'AFRICA', CI: 'AFRICA',
  CM: 'AFRICA', GA: 'AFRICA', GH: 'AFRICA', GN: 'AFRICA', MW: 'AFRICA',
  RW: 'AFRICA', SL: 'AFRICA', SN: 'AFRICA', TG: 'AFRICA', TZ: 'AFRICA',
  UG: 'AFRICA', ZM: 'AFRICA',
  FR: 'EUROPE', BE: 'EUROPE', CH: 'EUROPE', DE: 'EUROPE', GB: 'EUROPE',
  US: 'NORTH AMERICA', CA: 'NORTH AMERICA',
};

const resolveCountryLabel = (code: string): string => {
  if (code === '*') return 'Mondial';
  if (CONTINENT_LABELS[code]) return CONTINENT_LABELS[code];
  return countries.getName(code, 'fr') ?? code;
};

const formatCountries = (codes: string[]): string =>
  codes.map(c => `${resolveCountryLabel(c)} · ${c}`).join(', ');

const extractFilterableCountries = (providerList: IPaymentProvider[]): string[] => {
  const seen = new Set<string>();
  for (const provider of providerList) {
    for (const code of provider.countries) {
      if (code !== '*' && !CONTINENT_LABELS[code]) seen.add(code);
    }
  }
  return Array.from(seen).sort();
};

const normalizeEnabled = (provider: IPaymentProvider): IPaymentProvider => ({
  ...provider,
  enabled: provider.enabled !== false,
});

const hasRealChanges = (local: IPaymentProvider[], server: IPaymentProvider[]): boolean => {
  if (local.length !== server.length) return true;
  return local.some((localProvider, i) => localProvider.enabled !== server[i].enabled);
};

/* Returns the section key for a provider in the grouped view.
   Single specific country → that country code.
   Multi-country or wildcard/continent → bucketed at the end. */
const getSectionKey = (provider: IPaymentProvider): string => {
  const specificCodes = provider.countries.filter(
    c => c !== '*' && !CONTINENT_LABELS[c]
  );
  const hasWildcardOrContinent = provider.countries.some(
    c => c === '*' || CONTINENT_LABELS[c]
  );
  if (hasWildcardOrContinent) return '__mondial__';
  if (specificCodes.length === 1) return specificCodes[0];
  return '__multi__';
};

type TSection = { key: string; label: string; providers: IPaymentProvider[] };

const buildSections = (providerList: IPaymentProvider[]): TSection[] => {
  const map = new Map<string, IPaymentProvider[]>();

  for (const provider of providerList) {
    const key = getSectionKey(provider);
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(provider);
  }

  const sections: TSection[] = [];

  /* Country sections sorted alphabetically by name */
  Array.from(map.entries())
    .filter(([key]) => key !== '__multi__' && key !== '__mondial__')
    .sort(([a], [b]) => resolveCountryLabel(a).localeCompare(resolveCountryLabel(b), 'fr'))
    .forEach(([key, sectionProviders]) => {
      sections.push({
        key,
        label: `${resolveCountryLabel(key)} · ${key}`,
        providers: sectionProviders,
      });
    });

  if (map.has('__multi__')) {
    sections.push({ key: '__multi__', label: 'Multi-pays', providers: map.get('__multi__')! });
  }
  if (map.has('__mondial__')) {
    sections.push({ key: '__mondial__', label: 'Mondial', providers: map.get('__mondial__')! });
  }

  return sections;
};

type TTypeFilter = 'all' | PaymentProviderTypeEnum;
const ALL = 'all';

const PaymentProvidersPage = () => {

  const { providers: rawProviders, isLoading } = useRequestGetPaymentProviders();
  const { triggerSetPaymentProviders, isLoadingSetPaymentProviders } = useRequestSetPaymentProviders();
  const { triggerDeletePaymentOperator, isLoadingDeletePaymentOperator } = useRequestDeletePaymentOperator();

  const [serverProviders, setServerProviders] = useState<IPaymentProvider[]>([]);
  const [localProviders, setLocalProviders] = useState<IPaymentProvider[]>([]);
  const [typeFilter, setTypeFilter] = useState<TTypeFilter>(ALL);
  const [countryFilter, setCountryFilter] = useState<string>(ALL);

  const [formModal, setFormModal] = useState<{ open: boolean; provider?: IPaymentProvider }>({ open: false });
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; providerKey?: string }>({ open: false });

  useEffect(() => {
    const normalized = rawProviders.map(normalizeEnabled);
    setServerProviders(normalized);
    setLocalProviders(normalized);
  }, [rawProviders]);

  const hasChanges = hasRealChanges(localProviders, serverProviders);

  const filterableCountries = useMemo(
    () => extractFilterableCountries(localProviders),
    [localProviders]
  );

  const visibleProviders = useMemo(() => {
    return localProviders.filter(provider => {
      if (typeFilter !== ALL && provider.type !== typeFilter) return false;
      if (countryFilter !== ALL) {
        const continent = ISO2_TO_CONTINENT[countryFilter] ?? null;
        const matchesCountry =
          provider.countries.includes(countryFilter) ||
          provider.countries.includes('*') ||
          (continent !== null && provider.countries.includes(continent));
        if (!matchesCountry) return false;
      }
      return true;
    });
  }, [localProviders, typeFilter, countryFilter]);

  const isGrouped = countryFilter === ALL;

  const sections = useMemo(
    () => isGrouped ? buildSections(visibleProviders) : [],
    [isGrouped, visibleProviders]
  );

  const handleToggle = (key: string, checked: boolean) => {
    setLocalProviders(current =>
      current.map(provider =>
        providerKey(provider) === key
          ? { ...provider, enabled: checked }
          : provider
      )
    );
  };

  const handleSave = async () => {
    await triggerSetPaymentProviders(localProviders)
      .then(() => {
        setServerProviders(localProviders.map(normalizeEnabled));
        notify('Moyens de paiement mis à jour', 'success');
      })
      .catch(emptyFunction);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.providerKey) return;
    try {
      await triggerDeletePaymentOperator(deleteConfirm.providerKey);
      setDeleteConfirm({ open: false });
    } catch {
      /* error already surfaced via notify() in the hook */
    }
  };

  const isSaving = isLoadingSetPaymentProviders;
  const isDeleting = isLoadingDeletePaymentOperator;

  const renderRow = (provider: IPaymentProvider, hideCountry = false) => (
    <TableRow
      key={providerKey(provider)}
      className={!provider.enabled ? 'opacity-50' : undefined}>
      <TableCell>
        <img
          src={provider.logo}
          alt={provider.provider}
          className='h-7 w-7 rounded object-contain'
          onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
        />
      </TableCell>
      <TableCell className='text-sm'>
        {provider.provider}
      </TableCell>
      <TableCell>
        <ProviderBadge provider={provider.platform} />
      </TableCell>
      {!hideCountry && (
        <TableCell className='text-sm text-muted-foreground whitespace-nowrap'>
          {formatCountries(provider.countries)}
        </TableCell>
      )}
      <TableCell>
        <Badge
          variant='outline'
          className={
            provider.type === 'local'
              ? 'bg-blue-50 text-blue-700 border-blue-200'
              : 'bg-purple-50 text-purple-700 border-purple-200'
          }>
          {provider.type}
        </Badge>
      </TableCell>
      <TableCell className='text-right'>
        <Switch
          checked={provider.enabled}
          disabled={isSaving}
          onCheckedChange={checked => handleToggle(providerKey(provider), checked)}
        />
      </TableCell>
      <TableCell className='text-right'>
        <div className='flex items-center justify-end gap-1'>
          <Button
            variant='ghost'
            size='icon'
            className='h-7 w-7'
            disabled={isSaving || isDeleting}
            onClick={() => setFormModal({ open: true, provider })}>
            <Pencil className='h-5 w-5' />
          </Button>
          <Button
            variant='ghost'
            size='icon'
            className='h-7 w-7 text-destructive hover:text-destructive'
            disabled={isDeleting}
            onClick={() => setDeleteConfirm({ open: true, providerKey: provider.provider })}>
            <Trash2 className='h-5 w-5' />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );

  const renderTableHeader = (showCountry: boolean) => (
    <TableHeader>
      <TableRow>
        <TableHead className='w-14'>Logo</TableHead>
        <TableHead>Opérateur</TableHead>
        <TableHead>Plateforme</TableHead>
        {showCountry && <TableHead className='w-64'>Pays</TableHead>}
        <TableHead className='w-28'>Type</TableHead>
        <TableHead className='text-right'>Activé</TableHead>
        <TableHead className='text-right w-20'>Actions</TableHead>
      </TableRow>
    </TableHeader>
  );

  return (
    <>
    <div className='flex flex-col space-y-4 md:space-y-6 animate__animated animate__fadeIn'>
      <HeaderPage
        title='Moyens de paiement'
        description='Activez ou désactivez les moyens de paiement disponibles pour vos utilisateurs.' />

      {isLoading && (
        <div className='flex flex-col space-y-2'>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className='h-12 rounded-md bg-muted animate-pulse' />
          ))}
        </div>
      )}

      {!isLoading && localProviders.length === 0 && (
        <p className='text-sm text-muted-foreground'>
          Aucun moyen de paiement configuré.
        </p>
      )}

      {!isLoading && localProviders.length > 0 && (
        <>
          <div className='flex flex-wrap gap-3 items-center justify-between'>
          <div className='flex flex-wrap gap-3'>
            <Select value={typeFilter} onValueChange={v => setTypeFilter(v as TTypeFilter)}>
              <SelectTrigger className='w-44'>
                <SelectValue placeholder='Type' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Tous les types</SelectItem>
                <SelectItem value='local'>Local</SelectItem>
                <SelectItem value='international'>International</SelectItem>
              </SelectContent>
            </Select>

            <Select value={countryFilter} onValueChange={setCountryFilter}>
              <SelectTrigger className='w-56'>
                <SelectValue placeholder='Pays' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Tous les pays</SelectItem>
                {filterableCountries.map(code => (
                  <SelectItem key={code} value={code}>
                    {resolveCountryLabel(code)} · {code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
            <Button
              size='sm'
              onClick={() => setFormModal({ open: true, provider: undefined })}
              disabled={isSaving}>
              <Plus className='h-4 w-4 mr-1' />
              Ajouter
            </Button>
          </div>

          {visibleProviders.length === 0 && (
            <p className='text-sm text-muted-foreground'>
              Aucun résultat pour ces filtres.
            </p>
          )}

          {visibleProviders.length > 0 && isGrouped && (
            <Table>
              {renderTableHeader(false)}
              <TableBody>
                {sections.map(section => (
                  <Fragment key={section.key}>
                    <TableRow className='hover:bg-muted/30 bg-muted/30 border-t'>
                      <TableCell colSpan={6} className='py-2 px-4'>
                        <span className='text-xs font-semibold text-foreground/60 uppercase tracking-widest'>
                          {section.label}
                        </span>
                      </TableCell>
                    </TableRow>
                    {section.providers.map(p => renderRow(p, true))}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          )}

          {visibleProviders.length > 0 && !isGrouped && (
            <Table>
              {renderTableHeader(true)}
              <TableBody>
                {visibleProviders.map(p => renderRow(p, false))}
              </TableBody>
            </Table>
          )}

          {hasChanges && (
            <div className='flex justify-end pt-2'>
              <Button
                onClick={handleSave}
                disabled={isSaving}>
                {isSaving ? 'Enregistrement...' : 'Enregistrer les modifications'}
              </Button>
            </div>
          )}
        </>
      )}
    </div>

      <OperatorFormModal
        open={formModal.open}
        onClose={() => setFormModal({ open: false })}
        initialValues={formModal.provider}
      />

      <Dialog
        open={deleteConfirm.open}
        onOpenChange={open => !open && setDeleteConfirm({ open: false })}>
        <DialogContent className='sm:max-w-[400px] duration-0 animate-none'>
          <DialogHeader>
            <DialogTitle className='text-destructive'>Supprimer l&apos;opérateur</DialogTitle>
          </DialogHeader>
          <p className='text-sm text-muted-foreground py-2'>
            Êtes-vous sûr de vouloir supprimer&nbsp;
            <span className='font-semibold text-foreground'>{deleteConfirm.providerKey}</span>&nbsp;?
            Cette action est irréversible.
          </p>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setDeleteConfirm({ open: false })}
              disabled={isLoadingDeletePaymentOperator}>
              Annuler
            </Button>
            <Button
              variant='destructive'
              onClick={handleDeleteConfirm}
              disabled={isLoadingDeletePaymentOperator}>
              <Trash2 className='h-4 w-4 mr-2' />
              {isLoadingDeletePaymentOperator ? 'Suppression...' : 'Supprimer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

};

export default PaymentProvidersPage;
